import React, { useState } from "react";
import cx from "classnames";
import { PlayIcon, VolumeHighIcon, CameraIcon } from "assets";
import { UserManager } from "core";
import { useTranslation } from "i18n";
import FullScreenMediaViewer from "pages/Profile/components/common/FullScreenMediaViewer";
import MediaPlayer from "pages/Profile/components/common/MediaPlayer";
import { MediaItem } from "pages/Profile/types";
import { MediaType } from "shared-types";
import { getMediaViewerUrl } from "services/UrlBuilder";
import getImageAltText from "pages/Profile/utils/getImageAltText";

interface Props {
  toggleFullScreenViewer?: () => void;
  selectedIndex?: number;
  media: MediaItem[];
}

const iconStyles =
  "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-profile text-white";

const MediaThumbnailContent = ({ item }: { item: MediaItem }) => {
  const { t } = useTranslation();

  if (item.isTranscodeError) {
    if (item.mediaType === MediaType.Video) {
      return (
        <CameraIcon
          height={24}
          width={24}
          className={cx(iconStyles, "opacity-50")}
        />
      );
    }

    return (
      <VolumeHighIcon
        height={24}
        width={24}
        className={cx(iconStyles, "opacity-50")}
      />
    );
  }

  if (item.mediaType === MediaType.Video && item.poster) {
    return (
      <>
        <img
          src={item.poster}
          alt={getImageAltText({
            t,
            description: item.title,
          })}
          className="h-full w-full rounded-lg object-cover"
        />
        <PlayIcon height={24} width={24} className={iconStyles} />
      </>
    );
  }

  return <VolumeHighIcon height={24} width={24} className={iconStyles} />;
};

const MediaViewer = ({
  toggleFullScreenViewer,
  selectedIndex,
  media,
}: Props) => {
  const { t } = useTranslation();
  const [fullScreenMediaIndex, setFullScreenMediaIndex] = useState(
    selectedIndex ?? 0
  );
  const selectedMedia = media[fullScreenMediaIndex];

  return (
    <FullScreenMediaViewer onToggle={toggleFullScreenViewer}>
      <div className="flex min-h-screen flex-col items-center justify-center p-4 sm:p-8">
        <div className="relative flex aspect-video w-full max-w-6xl flex-col items-center">
          {selectedMedia?.isTranscodeError ? (
            <div className="flex aspect-video w-full flex-col items-center justify-center rounded-2xl bg-black/50 px-6 text-center text-sm text-white">
              {t("performer:profile.media.player.error")}
            </div>
          ) : (
            <MediaPlayer
              src={selectedMedia?.src}
              poster={selectedMedia?.poster}
              title={selectedMedia?.title}
              externalLink={
                UserManager.artistRef &&
                selectedMedia &&
                getMediaViewerUrl({
                  assetType: selectedMedia.mediaType,
                  artistRef: UserManager.artistRef,
                  startClipId: selectedMedia.id,
                })
              }
            />
          )}
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          {media.map((media, index) => {
            return (
              <button
                key={`media-button-${index}`}
                className={cx(
                  "relative aspect-video h-24 rounded-lg text-white opacity-60",
                  "bg-[linear-gradient(180deg,#2741E8_0%,#20016B_100%)] hover:opacity-100",
                  {
                    "opacity-100 ring-2 ring-white":
                      fullScreenMediaIndex === index,
                  }
                )}
                onClick={() => setFullScreenMediaIndex(index)}
              >
                <MediaThumbnailContent item={media} />
                <div className="absolute bottom-0 w-full truncate p-2">
                  {media.title}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </FullScreenMediaViewer>
  );
};

export default MediaViewer;
